/**
 * Utilitaires couleurs — génération d'une échelle Tailwind à partir d'une
 * couleur principale hex (US-TR-06).
 *
 * Implémentation simple en HSL : on calcule la teinte + saturation depuis
 * la couleur hex, puis on dérive les nuances 50→900 en modulant la
 * luminosité. Convient pour la majorité des cas — pas aussi sophistiqué
 * que `tailwindcss/colors` mais suffisant pour un branding par tenant.
 */

interface RGB {
  r: number;
  g: number;
  b: number;
}
interface HSL {
  h: number;
  s: number;
  l: number;
}

export function hexToRgb(hex: string): RGB {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return { r, g, b };
}

export function rgbToHex({ r, g, b }: RGB): string {
  const toHex = (v: number) => v.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
        break;
      case gn:
        h = ((bn - rn) / d + 2) * 60;
        break;
      case bn:
        h = ((rn - gn) / d + 4) * 60;
        break;
    }
  }
  return { h, s, l };
}

export function hslToRgb({ h, s, l }: HSL): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (h < 60) [r1, g1, b1] = [c, x, 0];
  else if (h < 120) [r1, g1, b1] = [x, c, 0];
  else if (h < 180) [r1, g1, b1] = [0, c, x];
  else if (h < 240) [r1, g1, b1] = [0, x, c];
  else if (h < 300) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

/** Mapping shade → luminosité cible (HSL.l) à appliquer. */
const SHADE_LIGHTNESS: Record<number, number> = {
  50: 0.97,
  100: 0.93,
  200: 0.85,
  300: 0.75,
  400: 0.6,
  500: 0.45,
  600: 0.38,
  700: 0.3,
  800: 0.22,
  900: 0.15,
};

/**
 * Génère une échelle Tailwind 50→900 à partir d'une couleur hex.
 * La teinte et la saturation sont préservées ; seule la luminosité varie.
 */
export function generateShades(hex: string): Record<number, string> {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  // Saturation atténuée pour les nuances très claires/foncées (rend plus naturel).
  const result: Record<number, string> = {};
  for (const [shadeStr, lightness] of Object.entries(SHADE_LIGHTNESS)) {
    const shade = Number(shadeStr);
    const sat = shade <= 100 ? hsl.s * 0.5 : shade >= 800 ? hsl.s * 0.95 : hsl.s;
    result[shade] = rgbToHex(hslToRgb({ h: hsl.h, s: sat, l: lightness }));
  }
  return result;
}

/** Applique l'échelle au DOM en injectant les CSS variables Tailwind. */
export function applyColorPalette(
  prefix: 'primary' | 'secondary' | 'accent',
  baseHex: string,
): void {
  const shades = generateShades(baseHex);
  for (const [shade, hex] of Object.entries(shades)) {
    document.documentElement.style.setProperty(`--color-${prefix}-${shade}`, hex);
  }
}
